<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: 'ROI Calculator', to: '/reports/roi-calculator' }
    ]"
  ></PageHeader>
  <div class="mx-auto max-w-7xl p-4 md:p-6">
    <RoiCalculatorComponent
      subtitle="Use the sliders or direct inputs to update your projections"
      main-output-key="roi"
      :input-configs="inputConfigs"
      :output-configs="outputConfigs"
    />
  </div>
</template>

<script setup lang="ts">
// components
import RoiCalculatorComponent from '@/components/unique/RoiCalculator.vue'
import type { RoiInputConfig, RoiOutputConfig } from '@/components/unique/RoiCalculator.vue'

import PageHeader from '@/components/page/PageHeader.vue'
/**
 * Input controls for the first ROI calculator variant.
 */
const inputConfigs: RoiInputConfig[] = [
  {
    key: 'appointmentVolume',
    label: 'Appointment Volume',
    min: 0,
    max: 200,
    step: 1,
    defaultValue: 25,
    decimals: 0
  },
  {
    key: 'deals',
    label: 'Deals',
    min: 0,
    max: 200,
    step: 1,
    defaultValue: 11,
    decimals: 0
  },
  {
    key: 'avgProfitPerUnit',
    label: 'Avg Profit per Unit',
    min: 0,
    max: 10000,
    step: 50,
    defaultValue: 1450,
    decimals: 0,
    prefix: '£'
  },
  {
    key: 'spend',
    label: 'Spend',
    min: 0,
    max: 10000,
    step: 50,
    defaultValue: 975,
    decimals: 0,
    prefix: '£'
  }
]

/**
 * Output formulas for ROI and supporting metrics.
 */
const outputConfigs: RoiOutputConfig[] = [
  {
    key: 'conversionRate',
    label: 'Avg Conversion Rate',
    format: 'percent',
    decimals: 1,
    calculate: (inputs: Record<string, number>) => {
      try {
        if (inputs.appointmentVolume <= 0) {
          return 0
        }
        return (inputs.deals / inputs.appointmentVolume) * 100
      } catch (error) {
        console.error('Failed to calculate conversion rate', error)
        return 0
      }
    }
  },
  {
    key: 'roi',
    label: 'ROI',
    format: 'currency',
    decimals: 0,
    calculate: (inputs: Record<string, number>) => {
      try {
        return inputs.deals * inputs.avgProfitPerUnit - inputs.spend
      } catch (error) {
        console.error('Failed to calculate ROI', error)
        return 0
      }
    }
  },
  {
    key: 'costPerSale',
    label: 'Cost per Sale',
    format: 'currency',
    decimals: 2,
    calculate: (inputs: Record<string, number>) => {
      try {
        if (inputs.deals <= 0) {
          return 0
        }
        return inputs.spend / inputs.deals
      } catch (error) {
        console.error('Failed to calculate cost per sale', error)
        return 0
      }
    }
  }
]
</script>
